// src/hooks/useNumberValidation.js
import { useCallback } from 'react';

export const useNumberValidation = () => {
  const validateNumberInput = useCallback((value, allowDecimals = false) => {
    if (typeof value !== 'string') return value;
    
    // Remove any non-numeric characters except decimal point if allowed
    let cleanValue = value.replace(/[^\d.]/g, '');
    
    if (!allowDecimals) {
      cleanValue = cleanValue.replace(/\./g, '');
    } else {
      // Only allow one decimal point
      const parts = cleanValue.split('.');
      if (parts.length > 2) {
        cleanValue = parts[0] + '.' + parts.slice(1).join('');
      }
    }
    
    return cleanValue;
  }, []);

  const validatePositiveNumber = useCallback((value, allowDecimals = false) => {
    const cleanValue = validateNumberInput(value, allowDecimals);
    
    // Ensure the value is positive
    const numValue = parseFloat(cleanValue);
    if (numValue < 0) {
      return '0';
    }
    
    return cleanValue;
  }, [validateNumberInput]);

  const validateIntegerInput = useCallback((value) => {
    return validateNumberInput(value, false);
  }, [validateNumberInput]);

  const validateDecimalInput = useCallback((value, decimalPlaces = 2) => {
    const cleanValue = validateNumberInput(value, true);
    
    // Limit decimal places if specified
    if (decimalPlaces > 0 && cleanValue.includes('.')) {
      const parts = cleanValue.split('.');
      if (parts[1] && parts[1].length > decimalPlaces) {
        return parts[0] + '.' + parts[1].substring(0, decimalPlaces);
      }
    }
    
    return cleanValue;
  }, [validateNumberInput]);

  const validatePriceInput = useCallback((value) => {
    // Price should be positive decimal with max 2 decimal places
    const cleanValue = validatePositiveNumber(value, true);
    return validateDecimalInput(cleanValue, 2);
  }, [validatePositiveNumber, validateDecimalInput]);

  const validateStockInput = useCallback((value) => {
    // Stock should be positive integer
    return validatePositiveNumber(value, false);
  }, [validatePositiveNumber]);

  return {
    validateNumberInput,
    validatePositiveNumber,
    validateIntegerInput,
    validateDecimalInput,
    validatePriceInput,
    validateStockInput
  };
};
